import { MODIFIER_LABEL } from './platform-modifier'

const SCHEME_URL_REGEX = /\bhttps?:\/\/[^\s<>"')]+/gi

const TLDS =
  'com|org|net|io|co|ai|app|dev|so|info|me|gov|edu|mil|us|uk|ca|de|fr|es|it|jp|cn|in|br|ru|au|nl|se|no|fi|dk|tv|fm|ly|gg|sh|pl|cz|tr|gr|kr|hk|sg|nz|mx|ar|ie|be|at|ch|pt|cloud|page|site|link|tools|design|xyz|tech|new'

const BARE_DOMAIN_REGEX = new RegExp(
  String.raw`(?<![@\w])((?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+(?:${TLDS}))(?![a-z0-9-])(\/[^\s<>"')]*)?`,
  'gi',
)

export interface ExtractedUrl {
  url: string
  domain: string
  start: number
  end: number
}

function getDomain(url: string): string | null {
  try {
    const { hostname } = new URL(url)
    return hostname.replace(/^www\./, '')
  } catch {
    return null
  }
}

function trimTrailingPunctuation(s: string): string {
  return s.replace(/[.,;:!?)\]"']+$/, '')
}

export function extractUrls(text: string): Array<ExtractedUrl> {
  if (!text) return []

  const result: Array<ExtractedUrl> = []
  const seen = new Set<string>()
  const occupied: Array<[number, number]> = []

  for (const match of text.matchAll(SCHEME_URL_REGEX)) {
    const raw = trimTrailingPunctuation(match[0])
    const start = match.index ?? 0
    const end = start + raw.length
    const domain = getDomain(raw)
    if (!domain) continue
    if (seen.has(raw)) continue
    seen.add(raw)
    occupied.push([start, end])
    result.push({ url: raw, domain, start, end })
  }

  for (const match of text.matchAll(BARE_DOMAIN_REGEX)) {
    const start = match.index ?? 0
    const raw = trimTrailingPunctuation(match[0])
    const end = start + raw.length
    if (occupied.some(([s, e]) => start >= s && end <= e)) continue
    const normalized = `https://${raw}`
    const domain = getDomain(normalized)
    if (!domain) continue
    if (seen.has(normalized)) continue
    seen.add(normalized)
    occupied.push([start, end])
    result.push({ url: normalized, domain, start, end })
  }

  result.sort((a, b) => a.start - b.start)
  return result
}

export const URL_HIGHLIGHT_CLASS = [
  'text-foreground-secondary underline decoration-foreground-disabled underline-offset-2',
  'box-decoration-clone py-1',
  '[.modifier-held_&]:cursor-pointer',
  '[&::after]:content-[attr(data-hint)]',
  '[&::after]:ml-1.5 [&::after]:inline-block [&::after]:align-middle [&::after]:px-1.5 [&::after]:py-0',
  '[&::after]:rounded-md [&::after]:corner-smooth [&::after]:bg-element [&::after]:border [&::after]:border-border',
  '[&::after]:type-ui-xs [&::after]:font-semibold [&::after]:text-foreground-secondary [&::after]:no-underline [&::after]:leading-[14px]',
  '[&::after]:opacity-0 [&::after]:transition-opacity [&::after]:duration-150',
  'hover:[&::after]:opacity-100',
].join(' ')

export const URL_HINT_TEXT = `${MODIFIER_LABEL} + click`

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

function escapeAttr(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

export function buildHtmlWithUrlHighlights(text: string): string {
  const urls = extractUrls(text)
  if (urls.length === 0) return escapeHtml(text)

  let result = ''
  let cursor = 0
  for (const u of urls) {
    if (u.start < cursor) continue
    result += escapeHtml(text.slice(cursor, u.start))
    result += `<span class="${URL_HIGHLIGHT_CLASS}" data-url="${escapeAttr(u.url)}" data-hint="${escapeAttr(URL_HINT_TEXT)}">${escapeHtml(text.slice(u.start, u.end))}</span>`
    cursor = u.end
  }
  result += escapeHtml(text.slice(cursor))
  return result
}
